/**
 * @file     rentalCardParts.tsx
 * @readme   ./CLAUDE.md
 * @purpose  The pieces the rental card variant adds to the bare card anatomy
 *           (design 3a): RentPrice with a muted "/mo", the AvailabilityBadge
 *           glass pill, pets, furnished and short-term icons with labels, the
 *           FairRentChip (only when a band is supplied), the solid "Listed by
 *           Ray Rasouli" tag and the LicenseeAttribution line on owner cards.
 */
'use client';

import '@/core/i18n/deferred/rentalsSearch';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { layoutTokens } from '@/design-system/tokens/layout';
import { semanticRoleColors } from '@/design-system/tokens/semanticColors';
import { tint } from '@/design-system/tokens/tint';
import { hubColors } from '@/components/hub/hubKitStyles';
import { ListingIcon } from '@/components/icons/ListingIcons';
import { brandConfig } from '@/core/config/brand/brandConfig';
import { formatRent } from '@/utils/rentFormat';
import { CARD_NARROW_QUERY, CARD_OVERLAY_INSET, PillDot, glassPillSx, statusBadgePlacementSx } from './cardChrome';
import { availabilityOf, type FairRentBand, type RentalFeatures } from '../../services/rentalCardModel';

/** "$2,850/mo": 20/600 tabular, the unit muted. `row` is the ListingRow size (19). */
export function RentPrice({ amount, size = 'card' }: { amount: number | null; size?: 'card' | 'row' | 'compact' }) {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const fontSize = size === 'row' ? 19 : size === 'compact' ? 18 : 20;
  return (
    <Box
      component="p"
      data-testid="rent-price"
      sx={{
        m: 0,
        fontSize,
        fontWeight: 600,
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
        fontVariantNumeric: 'tabular-nums',
        color: theme.semanticColors.textPrimary,
        whiteSpace: 'nowrap',
        ...(size === 'card' ? { [CARD_NARROW_QUERY]: { fontSize: 18 } } : null),
      }}
    >
      {amount != null ? (
        <>
          {formatRent(amount)}
          <Box component="span" sx={{ ml: '2px', fontSize: 13.5, fontWeight: 500, letterSpacing: 0, color: theme.semanticColors.textMuted }}>
            {t('rentalsSearch.card.perMonth', { defaultValue: '/mo' })}
          </Box>
        </>
      ) : (
        t('rentalsSearch.card.rentOnRequest', { defaultValue: 'Rent on request' })
      )}
    </Box>
  );
}

/** "Available now" (success dot) or "Available Nov 1" (primary dot), bottom left of the photo. */
export function AvailabilityBadge({
  availableOn,
  now,
  hasPhotoCount,
  size = 'card',
}: {
  availableOn: string | null | undefined;
  now: Date | null;
  hasPhotoCount: boolean;
  size?: 'card' | 'row';
}) {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const availability = availabilityOf(availableOn, now);
  const label = availability.kind === 'now'
    ? t('rentalsSearch.card.availableNow', { defaultValue: 'Available now' })
    : `${t('rentalsSearch.card.availablePrefix', { defaultValue: 'Available' })} ${availability.label}`;
  return (
    <Box
      data-testid="card-status-badge"
      data-badge={availability.kind === 'now' ? 'available-now' : 'available-date'}
      sx={{
        ...glassPillSx(theme),
        ...statusBadgePlacementSx(hasPhotoCount),
        ...(size === 'row' ? { height: 24, fontSize: 11.5 } : null),
        // The count hides on a phone 2-up card, so the badge may take the width.
        ...(hasPhotoCount ? { maxWidth: { xs: `calc(100% - ${CARD_OVERLAY_INSET * 2}px)`, sm: 'calc(100% - 80px)' } } : null),
      }}
    >
      <PillDot color={availability.kind === 'now' ? theme.semanticColors.statusSuccess : theme.semanticColors.interactivePrimary} />
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
    </Box>
  );
}

/** Pets OK, Furnished, Short term: line icons with labels; nothing when none apply. */
export function RentalFeatureIcons({ features }: { features: RentalFeatures | undefined }) {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  if (!features) return null;
  const items = [
    features.pets ? { key: 'pets', icon: 'paw' as const, label: t('rentalsSearch.card.pets', { defaultValue: 'Pets OK' }) } : null,
    features.furnished ? { key: 'furnished', icon: 'sofa' as const, label: t('rentalsSearch.card.furnished', { defaultValue: 'Furnished' }) } : null,
    features.shortTerm ? { key: 'short', icon: 'calendar' as const, label: t('rentalsSearch.card.short', { defaultValue: 'Short term' }) } : null,
  ].filter((item): item is NonNullable<typeof item> => item != null);
  if (items.length === 0) return null;
  return (
    <Box
      component="ul"
      data-testid="rental-features"
      aria-label={t('rentalsSearch.card.features', { defaultValue: 'Rental features' })}
      sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25 }}
    >
      {items.map((item) => (
        <Box
          component="li"
          key={item.key}
          data-feature={item.key}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 12.5,
            lineHeight: 1.4,
            color: theme.semanticColors.textSecondary,
            whiteSpace: 'nowrap',
            '& svg': { width: 13, height: 13, flexShrink: 0 },
          }}
        >
          <ListingIcon name={item.icon} size={16} />
          {item.label}
        </Box>
      ))}
    </Box>
  );
}

const FairGlyph = ({ band }: { band: FairRentBand }) => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {band === 'in' ? <path d="M5 12.5l4.5 4.5L19 7.5" /> : band === 'below' ? <path d="M12 5v14M6 13l6 6 6-6" /> : <path d="M12 19V5M6 11l6-6 6 6" />}
  </svg>
);

/** Where the rent sits against asking rents nearby. Rendered only when a band is passed; never fetched here. */
export function FairRentChip({ band }: { band: FairRentBand | null | undefined }) {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  if (!band) return null;
  const label = band === 'in'
    ? t('rentalsSearch.card.fair.in', { defaultValue: 'Fair rent' })
    : band === 'below'
      ? t('rentalsSearch.card.fair.below', { defaultValue: 'Below nearby rents' })
      : t('rentalsSearch.card.fair.above', { defaultValue: 'Above nearby rents' });
  const tone = band === 'in'
    ? { bg: hubColors.chip, fg: hubColors.chipText }
    : band === 'below'
      ? { bg: semanticRoleColors.successSoft, fg: theme.semanticColors.textPrimary }
      : { bg: tint(theme.semanticColors.statusWarning, 16), fg: theme.semanticColors.textPrimary };
  return (
    <Box
      component="span"
      data-testid="fair-rent-chip"
      data-band={band}
      sx={{
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        minHeight: 24,
        px: 1,
        borderRadius: `${layoutTokens.radius.pill}px`,
        backgroundColor: tone.bg,
        color: tone.fg,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1.3,
      }}
    >
      <FairGlyph band={band} />
      {label}
    </Box>
  );
}

/**
 * The solid owner tag in the Compare slot: "Listed by Ray Rasouli", "Listed
 * by Ray" when compact (about 232px), the house icon alone at 170px. The
 * accessible name is always the full line.
 */
export function OwnerListedTag({ size = 'card' }: { size?: 'card' | 'row' }) {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const full = t('rentalsSearch.card.listedByRay', { defaultValue: 'Listed by Ray Rasouli' });
  const short = t('rentalsSearch.card.listedByRayShort', { defaultValue: 'Listed by Ray' });
  const row = size === 'row';
  return (
    <Box
      data-testid="owner-listed-tag"
      role="img"
      aria-label={full}
      sx={{
        position: 'absolute',
        top: CARD_OVERLAY_INSET,
        left: CARD_OVERLAY_INSET,
        zIndex: 3,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        height: row ? 24 : 28,
        px: row ? '9px' : '12px',
        maxWidth: 'calc(100% - 66px)',
        borderRadius: `${layoutTokens.radius.pill}px`,
        backgroundColor: theme.semanticColors.interactivePrimary,
        color: theme.semanticColors.textOnPrimary,
        fontSize: row ? 11.5 : 12.5,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        pointerEvents: 'none',
        '& [data-tag-full], & [data-tag-short]': { overflow: 'hidden', textOverflow: 'ellipsis' },
        '& [data-tag-short]': { display: row ? 'inline' : 'none' },
        '& [data-tag-full]': { display: row ? 'none' : 'inline' },
        ...(row
          ? null
          : {
              '@container listing-card (max-width: 260px)': {
                '& [data-tag-full]': { display: 'none' },
                '& [data-tag-short]': { display: 'inline' },
              },
              '@container listing-card (max-width: 190px)': {
                width: 28,
                px: 0,
                justifyContent: 'center',
                '& [data-tag-full], & [data-tag-short]': { display: 'none' },
              },
            }),
      }}
    >
      <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0, '& svg': { width: 13, height: 13 } }}>
        <ListingIcon name="house" size={16} />
      </Box>
      <span data-tag-full aria-hidden>{full}</span>
      <span data-tag-short aria-hidden>{short}</span>
    </Box>
  );
}

/** Kind-agnostic licensee line in the attribution slot: a 28px photo, the licensee and the brokerage. Never clamped. */
export function LicenseeAttribution({ compact = false }: { compact?: boolean }) {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const name = t('rentalsSearch.card.licenseeName', { defaultValue: `${brandConfig.agentName}, REALTOR®` });
  const brokerage = t('rentalsSearch.card.brokerage', { defaultValue: brandConfig.brokerageName });
  if (compact) {
    return (
      <Box data-testid="licensee-attribution" sx={{ fontSize: 11.5, lineHeight: 1.45, color: theme.semanticColors.textMuted, overflowWrap: 'anywhere' }}>
        {name} · {brokerage}
      </Box>
    );
  }
  return (
    <Box
      data-testid="licensee-attribution"
      sx={{ mt: 'auto', pt: 2, display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}
    >
      <Box
        component="img"
        src="/prompts/ray-avatar-128.png"
        alt=""
        width={28}
        height={28}
        loading="lazy"
        decoding="async"
        sx={{ width: 28, height: 28, flexShrink: 0, borderRadius: `${layoutTokens.radius.pill}px`, backgroundColor: hubColors.chip, objectFit: 'cover' }}
      />
      <Box sx={{ minWidth: 0, fontSize: 11.5, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
        <Box component="span" sx={{ display: 'block', color: theme.semanticColors.textSecondary, fontWeight: 500 }}>{name}</Box>
        <Box component="span" sx={{ display: 'block', color: theme.semanticColors.textMuted }}>{brokerage}</Box>
      </Box>
    </Box>
  );
}
